import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import { Container, UnderlinedLink } from '../../components';
import { usePagination, useSorting } from '../../hooks';
import { DataTable, DataTableColumn } from '../../tables';
import { useDataCollection } from '../../hooks/useDataCollection';
import { AssociatedCompany } from '../../types';
import { CustomerShipToDrawer } from '../CustomerShipTo/CustomerShipToDrawer.component';
import { AgencyCustomersListProps } from './AgencyCustomersList.props';

export const AgencyCustomersList = ({
  associatedCompanies,
}: AgencyCustomersListProps) => {
  const { t } = useTranslation();
  const sorting = useSorting('companyName');
  const pagination = usePagination();
  const [selectedCustomer, setSelectedCustomer] =
    useState<AssociatedCompany | null>(null);

  const companies = useMemo(() => {
    if (!associatedCompanies) {
      return [];
    }

    return Object.values(associatedCompanies)
      .filter((a) => !a.isDeleted)
      .map((company) => {
        return {
          ...company,
          id: company.companyId,
          shipToAmount: company.customerAddresses.length,
          companyAccountNumber: company.companyAccountNumber || 'N/A',
        };
      });
  }, [associatedCompanies]);

  const { list: data } = useDataCollection(companies, sorting, pagination);

  const nameOutput = (id: string) => {
    const customer = associatedCompanies?.[id] || null;
    return (
      <UnderlinedLink onClick={() => setSelectedCustomer(customer)}>
        {customer?.companyName || 'N/A'}
      </UnderlinedLink>
    );
  };

  return (
    <Container sx={{ marginTop: 6 }}>
      <DataTable data={data} pagination={pagination} sorting={sorting}>
        <DataTableColumn
          title={t('agencies.fields.customer')}
          property="companyId"
          width="33%"
          output={nameOutput}
        />
        <DataTableColumn
          property="companyAccountNumber"
          title={t('companies.fields.accountNumber')}
          width="33%"
        />
        <DataTableColumn
          property="shipToAmount"
          title={t('agencies.fields.shipTo')}
          width="33%"
        />
      </DataTable>
      <CustomerShipToDrawer
        customer={selectedCustomer}
        onClose={() => setSelectedCustomer(null)}
      />
    </Container>
  );
};
